import { CartItem, CartItemGroup } from './cart.model';
import { ObjectUtils } from '../../../../../../api/src/_core/utils/utils.object';
import { ExportType } from "../../../../../../api/src/encode/models/job-def.dto";

export enum LocaleScope {
	ACTIVE = 'active', // only look within the currently active locale
	EVERY = 'every' // look within all locales
}

export class CartUtils {
	public static getExportTypesFromGroup(group: CartItemGroup): ExportType[] {
		const items = this.extractGroupItems(group);

		const types =  Array.from(new Set(items?.map(item => item.metadata?.['exportTypes'])?.flat().filter(Boolean)));
		return (types.length ? types : undefined) || group.parent?.exportTypes || items?.[0].metadata?.['exportTypes'] || [ExportType.VIDEO];
	}

	public static getFirstItemOfGroup(group: CartItemGroup): CartItem {
		if (group?.items?.length) {
			return group.items?.[0];
		} else if (group?.subgroups?.length) {
			return group.subgroups[0]?.items?.[0];
		} else return null;
	}

	public static extractGroupItems(group: CartItemGroup): CartItem[] {
		return [
			...(group?.items || []),
			...(group?.subgroups?.flatMap(subgroup => subgroup?.items) || []),
		];
	}

	public static extractItemsFromGroups(groups: CartItemGroup[]): CartItem[] {
		return [
			...groups?.flatMap(g => g.items),
			...groups?.flatMap(g => g.subgroups?.flatMap(subg => subg.items) || [])
		];
	}

	/**
	 * Based on the locale scope, this function returns the appropriate translation baseline for a given value.
	 * if the locale scope is ACTIVE, it will only return the value for the currently active locale.
	 * If the locale scope is EVERY, it will concatenate all translations for the value.
	 * @param value
	 * @param localeObj
	 * @param localeScope
	 * @private
	 */
	private static getTranslationBaseline(
		value: any,
		localeObj: any,
		localeScope: LocaleScope = LocaleScope.ACTIVE
	): string {
		if (localeScope === LocaleScope.ACTIVE) {
			return ObjectUtils.getLocale(value, localeObj);
		} else {
			if (!localeObj) {
				return value;
			}

			const localeSum = value + Object.values(localeObj)?.join('');

			if (localeSum) {
				return localeSum;
			} else return value;
		}
	}

	private static filterGroupItemsByQuery(
		group: CartItemGroup,
		query: string,
		localeScope: LocaleScope = LocaleScope.ACTIVE
	) {
		return group.items.filter(i => {
			const itemName = CartUtils.getTranslationBaseline(i.name, i.locale?.['name'], localeScope);
			return itemName.toLowerCase().includes(query);
		});
	}

	private static filterGroupSubgroupsByQuery(
		group: CartItemGroup,
		query: string,
		localeScope: LocaleScope = LocaleScope.ACTIVE
	) {
		return group.subgroups.reduce<CartItemGroup[]>((subAcc, subgroup) => {
			const subgroupName = CartUtils.getTranslationBaseline(subgroup.name, subgroup.locale?.group?.['name'], localeScope);

			if (subgroupName.toLowerCase().includes(query)) {
				subAcc.push(subgroup);
			} else if (subgroup.items) {
				const items = this.filterGroupItemsByQuery(subgroup, query, localeScope);

				if (items.length > 0) {
					subAcc.push({ ...subgroup, items: items });
				}
			}
			return subAcc;
		}, []);
	}

	public static filterGroupsByQuery(
		groups: CartItemGroup[],
		query: string,
		localeScope: LocaleScope = LocaleScope.ACTIVE,
		excludeSublevelMatches: boolean = false
	): CartItemGroup[] {
		if (!groups?.length || !query?.length) {
			return groups || [];
		}

		return groups.reduce<CartItemGroup[]>((acc, group) => {
			const firstItem = CartUtils.getFirstItemOfGroup(group);
			const groupName = CartUtils.getTranslationBaseline(group.name, firstItem.locale?.listing?.['name'], localeScope);

			if (groupName.toLowerCase().includes(query)) {
				if (excludeSublevelMatches) {
					acc.push({
						...group,
						items: [],
						subgroups: []
					});
				} else {
					acc.push(group);
				}

			} else {
				let visibleGroup: CartItemGroup = { ...group, items: [], subgroups: [] };

				// go through items
				if (group.items) {
					const items = this.filterGroupItemsByQuery(group, query, localeScope);

					if (items.length > 0) {
						visibleGroup = { ...visibleGroup, items: items };
					}
				}

				// go through subgroups
				if (group.subgroups) {
					const subgroups = this.filterGroupSubgroupsByQuery(group, query, localeScope);

					if (subgroups?.length > 0) {
						visibleGroup = {
							...visibleGroup,
							subgroups: subgroups
						};
					}
				}

				if (visibleGroup.items?.length || visibleGroup.subgroups?.length) {
					acc.push(visibleGroup);
				}
			}

			return acc;
		}, []);
	}

}
